import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { aCaptions, aCurrentCaption, aProgress, useAtomValue } from "~/atoms"
import { Caption } from "~/types"
import { cn, parseSrt } from "~/utils"

type SrtData = { start: number; end: number; text: string }[]

function useSrtData(captions: Caption[]) {
  const initialized = useRef(false)
  const [srtData, setSrtData] = useState<{ [key: string]: SrtData }>()

  const fetchAllSrts = useCallback(async () => {
    Promise.all(
      captions.map(async (caption) => {
        const text = await fetch(caption.srtUrl!).then((res) => res.text())
        setSrtData((prev) => ({ ...prev, [caption.language]: parseSrt(text) }))
      })
    )
  }, [captions])

  useEffect(() => {
    if (captions && !srtData && !initialized.current) {
      initialized.current = true
      fetchAllSrts()
    }
  }, [captions, srtData, fetchAllSrts])

  return srtData
}

export default function Captions({ className }: { className?: string }) {
  const currentCaption = useAtomValue(aCurrentCaption)
  const captions = useAtomValue(aCaptions)
  const progress = useAtomValue(aProgress)
  const srtData = useSrtData(captions)

  const currentCaptionText = useMemo(() => {
    if (
      srtData &&
      currentCaption &&
      typeof srtData[currentCaption!.language] !== "undefined"
    ) {
      const millisecondProgress = progress * 1000
      return srtData[currentCaption!.language].find((caption) => {
        return (
          millisecondProgress >= caption.start &&
          millisecondProgress <= caption.end
        )
      })?.text
    }
  }, [srtData, currentCaption, progress])

  if (!currentCaptionText) {
    return null
  }

  return (
    <div className={cn(className, "flex justify-center")}>
      <span className="mx-1 rounded-md bg-black/75 px-1 py-1.5 text-center text-xs text-white sm:px-3 sm:text-base">
        {currentCaptionText}
      </span>
    </div>
  )
}
